/**
 * 担当要件定義案件一覧カード（メンバー詳細 / マイページ）。
 *
 * 「担当開発案件一覧」（`AssignedProjectsCard`）の**上**に置く、要件定義フェーズ専用の一覧。
 * エンジニアが自分の要件定義タスクをまとめて見られる場所がどこにも無く、Backlog で子票を
 * 自力で検索するしかなかったため追加した。
 *
 * 開発側の一覧と列を揃えない（案件キー / 案件名 / ステータスの 3 列だけ）のは、要件定義フェーズの
 * 母票が Gen2 母票として確定していないため。種別が「アサイン前」のままで、進捗率・健康度・
 * 実績pt はどれも要件定義の実態を表さない。見出しは当月pt 内訳バーと同じ浅緑（要件定義の主題色）で、
 * 開発側の橙黄と対にして「どっちの一覧か」を色で読ませる。
 *
 * ステータス列は**母票のものではなく「要件定義フェーズのどこまで進んだか」**を出す。要件定義
 * フェーズの母票はステータスが `未対応` のまま動かないものが大半で、母票ステータスを出すと全行が
 * 同じ値になって読めない。進捗は API が 1 ラベルへ畳んだ結果（`requirementPhase`）をバッジにする
 * ——PI Lab に要件定義の記録がある案件はその工程（作成中 / AI レビュー / DLE レビュー待ち）、
 * 無い案件は Backlog の要件定義セット子票の完了状況から畳まれる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 担当要件定義案件一覧カード。自分が担当する要件定義成果物子票が未対応/処理中で残り、かつ要件定義フェーズが未終了の母票を 案件キー・案件名・要件定義の進捗 で一覧する。案件キーは案件詳細ページの要件定義タブ（/projects/$pjKey/spec）へ画面内遷移する。見出しは要件定義の主題色（浅緑）
 * @graph-connects REQUIREMENT_BAR_COLOR [references] 見出しに使う要件定義の主題色（当月pt 内訳バーと共通）
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { REQUIREMENT_BAR_COLOR } from '../member-pt-colors.js';
import type {
  MemberRequirementProjectListResponse,
  RequirementPhase,
  RequirementPhaseState,
} from '../../../types/project.js';

/**
 * 要件定義フェーズの進み方 → バッジの色。子チケットのステータスバッジ
 * （完了=success / 処理中=info / 未対応=warning）と同じ対応にして、同じ画面の中で色の意味を揃える。
 * @graph-connects none
 */
const PHASE_VARIANT: Record<RequirementPhaseState, 'success' | 'info' | 'warning' | 'default'> = {
  completed: 'success',
  inProgress: 'info',
  // 待ちは自分の手番ではないので、動かせる工程（info）と色で分ける。
  awaitingReview: 'default',
  notStarted: 'warning',
};

/**
 * 進み方 → i18n キー。`notStarted` は指す工程が無いので工程名を差し込まない。
 * @graph-connects none
 */
const PHASE_LABEL_KEY: Record<Exclude<RequirementPhaseState, 'notStarted'>, string> = {
  completed: 'members.detail.requirementPhase.completed',
  inProgress: 'members.detail.requirementPhase.inProgress',
  awaitingReview: 'members.detail.requirementPhase.awaitingReview',
};

/**
 * 要件定義フェーズの進捗バッジ。工程名を持つときだけ「{工程}完了」「{工程}処理中」
 * 「{工程}待ち」にし、どの工程も動いていなければ「未着手」。
 * @graph-connects none
 */
function RequirementPhaseBadge({ phase }: { phase: RequirementPhase }) {
  const { t } = useTranslation();
  const label =
    phase.label === null || phase.state === 'notStarted'
      ? t('members.detail.requirementPhase.notStarted')
      : t(PHASE_LABEL_KEY[phase.state], { phase: phase.label });
  return (
    <Badge variant={PHASE_VARIANT[phase.state]} size="sm">
      {label}
    </Badge>
  );
}

/** @graph-connects none */
interface AssignedRequirementProjectsCardProps {
  /** 担当要件定義案件一覧レスポンス。 */
  data: MemberRequirementProjectListResponse | undefined;
  /** 取得中フラグ。 */
  isLoading: boolean;
}

/** @graph-connects none */
export function AssignedRequirementProjectsCard({
  data,
  isLoading,
}: AssignedRequirementProjectsCardProps) {
  const { t } = useTranslation();
  const projects = data?.data ?? [];

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={
          <span style={{ color: REQUIREMENT_BAR_COLOR }}>
            {t('members.detail.assignedRequirementProjects')}
          </span>
        }
        description={t('members.detail.assignedRequirementProjectsDescription')}
        action={
          !isLoading && (
            <span className="whitespace-nowrap text-sm text-[var(--color-text-secondary)]">
              {t('members.detail.assignedCount', { count: projects.length })}
            </span>
          )
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-24 flex items-center justify-center">
            <div className="loading-spinner" />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-center py-8 text-[var(--color-text-muted)]">
            {t('members.detail.noRequirementProjects')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed">
              <colgroup>
                <col className="w-[180px]" />
                <col />
                <col className="w-[200px]" />
              </colgroup>
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.issueKey')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('projects.fields.summary')}
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)] whitespace-nowrap">
                    {t('members.detail.requirementPhase.column')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr
                    key={project.id}
                    className="border-b border-[var(--color-border)] last:border-b-0 hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="py-3 px-4 text-sm whitespace-nowrap">
                      {/*
                        **既定タブ（ダッシュボード）ではなく要件定義タブへ直接飛ばす**。
                        要件定義フェーズの母票は種別が「アサイン前」のままのものが大半で、
                        generation が NULL になるため project_detail_table（generation=2）にも
                        Gen1 フォールバック（generation=1）にも載らず、ダッシュボードタブは 404 する。
                        要件定義タブは PI Lab 側（Firestore）のデータだけで描けるので generation に
                        依存しない。この一覧の文脈（自分の要件定義を見に行く）とも一致する。
                        Backlog の票へは案件詳細ページのヘッダのリンクから 1 クリックで行ける。
                      */}
                      <Link
                        to="/projects/$pjKey/spec"
                        params={{ pjKey: project.issueKey }}
                        className="text-[var(--color-accent-primary)] hover:underline"
                      >
                        {project.issueKey}
                      </Link>
                    </td>
                    <td
                      className="py-3 px-4 text-sm text-[var(--color-text-primary)] truncate"
                      title={project.summary}
                    >
                      {project.summary}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <RequirementPhaseBadge phase={project.requirementPhase} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
